import { forwardRef, useImperativeHandle, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import OpcaoSelecao from '@/components/base/OpcaoSelecao';
import ConstrutorFormulario, {
  type ConstrutorFormularioHandle,
} from './ConstrutorFormulario';
import type { FormaParticipar } from '../types';

export interface PassoFichaHandle {
  validar: () => Promise<boolean>;
}

interface PassoFichaProps {
  participacoes: FormaParticipar[];
  onChange: (participacoes: FormaParticipar[]) => void;
}

function PassoFicha(
  { participacoes, onChange }: PassoFichaProps,
  ref: React.Ref<PassoFichaHandle>,
) {
  const refsForm = useRef(new Map<number, ConstrutorFormularioHandle>());

  useImperativeHandle(
    ref,
    () => ({
      async validar() {
        let ok = true;
        for (let i = 0; i < participacoes.length; i += 1) {
          const p = participacoes[i];
          if (p.temFormulario) {
            const handle = refsForm.current.get(i);
            if (handle) {
              const res = await handle.salvar();
              if (!res) ok = false;
            }
          }
        }
        return ok;
      },
    }),
    [participacoes],
  );

  function mudarTemFormulario(i: number, tem: boolean) {
    const p = participacoes[i];
    if (!tem && p.formularioId) {
      supabase
        .from('formularios')
        .update({ ativo: false })
        .eq('id', p.formularioId)
        .then(() => {});
    }
    onChange(participacoes.map((x, j) => (j === i ? { ...x, temFormulario: tem } : x)));
  }

  function aoCriarFormulario(i: number, id: number) {
    onChange(participacoes.map((x, j) => (j === i ? { ...x, formularioId: id } : x)));
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-normal text-foreground-950">A ficha de inscrição</h2>
        <p className="mt-1 text-sm text-secondary-500">
          As perguntas que a pessoa responde ao se inscrever.
        </p>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
          <i className="ri-information-line text-lg" />
        </span>
        <p className="text-sm text-foreground-800">
          Nome completo, CPF, data de nascimento e telefone já são pedidos pela plataforma antes
          da ficha. Não repita essas perguntas.
        </p>
      </div>

      {participacoes.map((p, i) => (
        <div key={i} className="rounded-lg border border-secondary-200 bg-white p-5">
          <div className="flex items-center gap-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-700">
              {i + 1}
            </span>
            <h3 className="text-base font-medium text-foreground-950">
              {p.nome.trim() || `tipo de inscrição ${i + 1}`}
            </h3>
          </div>

          <div className="mt-3">
            <span className="block text-[15px] font-medium text-foreground-950">
              Precisa fazer perguntas para quem se inscrever nesta opção?
            </span>
            <p className="mt-1 text-sm text-secondary-500">
              Crie um formulário quando precisar de informações além dos dados básicos do
              participante. Para um ingresso simples, você pode não precisar.
            </p>
            <div className="mt-1">
              <OpcaoSelecao
                tipo="radio"
                checked={!p.temFormulario}
                onChange={() => mudarTemFormulario(i, false)}
              >
                Não
              </OpcaoSelecao>
              <OpcaoSelecao
                tipo="radio"
                checked={p.temFormulario}
                onChange={() => mudarTemFormulario(i, true)}
              >
                Sim, quero criar um formulário
              </OpcaoSelecao>
            </div>
          </div>

          {p.temFormulario && p.id !== null && (
            <div className="mt-4">
              <ConstrutorFormulario
                ref={(el) => {
                  if (el) refsForm.current.set(i, el);
                  else refsForm.current.delete(i);
                }}
                modalidadeId={p.id}
                formularioId={p.formularioId}
                inscritos={p.inscritos}
                onFormularioCriado={(id) => aoCriarFormulario(i, id)}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default forwardRef(PassoFicha);